import { useEffect, useState, useRef, useCallback } from "react";

// Hooks
import useChartSlice from "store/slice/charts/useChartSlice";
import useChartResize from "../hooks/useChartResize";
import useChartRender from "../hooks/useChartRender";
import useCustomiseLabels from "../hooks/useCustomiseLabels";
import useBalanceWatermark from "./useBalanceWatermark";
import useBalanceHandlers from "./useBalanceHandlers";
import usePreviousState from "utils/hooks/usePreviousState";

// Types
import { TUseBalance } from "./Balance.types";
import { BubbleSerie, LD5Chart } from "store/slice/charts/chartsSlice.types";
import { TBriefChart } from "services/projects/project.types";
import { IBaseChartProps, IRefObjectForHighchartsReactWithName } from "components/Charts/chart.types";

// Config
import { getOption, isBubbleSerie } from "./BalanceConfig";
import { BRIEF_REFERENCE_CIRCLE_STYLES, CROSS_HAIR_CURSOR } from "../chart.constants";

// Image
import crossHairImg from "assets/images/svgs/chart-crosshair.svg";

// Utils
import { isEmpty, isEqual } from "lodash";
import theme from "assets/styles/themes";
import { renderDotCircles, updateSeriesWrapper } from "./balance.functions";
import { getProportionalRadius } from "../chart.functions";

declare module "highcharts" {
    interface PointerEventObject {
        xAxis: [{ value: number }];
        yAxis: [{ value: number }];
    }
}

const useBalance = ({
    width,
    height,
    getData,
    getbullEyeCoors,
    fromRefinement,
    moreOptions,
}: IBaseChartProps): TUseBalance => {
    const chartDataState = useChartSlice(getData) as LD5Chart;

    const [showRoles, setShowRoles] = useState(true);
    const [showNames, setShowNames] = useState(false);
    const [showAllLabels, setShowAllLabels] = useState(false);
    const [chartCircles, setChartCircles] = useState(true);
    const [hideAllLabels, setHideAllLabels] = useState(false);
    const [circle, setCircle] = useState<Partial<TBriefChart>>(moreOptions?.bullEyeCoors || { x: 0, y: 0, radius: 1 });

    const previousCircle = usePreviousState(circle);

    const visibleSeries = useRef([0]);
    const chartRef = useRef<IRefObjectForHighchartsReactWithName>(null);
    const isLegendClicked = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const circleRef = useRef<Highcharts.SVGElement | null>(null);
    const crossHairRef = useRef<Highcharts.SVGElement | null>(null);
    const elementsToDrawRef = useRef<(Highcharts.SVGElement | undefined)[]>([]);
    const dotCirclesRef = useRef<Highcharts.SVGElement[]>([]);

    const { renderArchetypes, toggleShowArchetypeVisibility, renderAxesTitles } = useBalanceWatermark(chartCircles);
    const [{ personDataElement, peopleDataElement }, { onClose, clickPoint, legendItemClick }] = useBalanceHandlers(
        chartDataState,
        visibleSeries,
        containerRef,
        isLegendClicked
    );

    const clickPointRef = useRef(clickPoint);
    const disableSeriesClickRef = useRef(moreOptions?.disableSeriesClick);

    useEffect(() => {
        clickPointRef.current = clickPoint;
        disableSeriesClickRef.current = moreOptions?.disableSeriesClick;
    }, [clickPoint, moreOptions?.disableSeriesClick]);

    const renderDots = useCallback((chart: Highcharts.Chart, isExporting?: boolean) => {
        renderDotCircles(chart, dotCirclesRef, {
            isExporting,
            onClickPoint:
                isExporting || disableSeriesClickRef.current
                    ? undefined
                    : function (this, event) {
                          clickPointRef.current.call(this, event);
                      },
        });
    }, []);

    useEffect(() => {
        return () => {
            dotCirclesRef.current.forEach((dotCircle) => dotCircle.destroy());
            dotCirclesRef.current = [];
        };
    }, []);

    const [showNoDataModal, setShowNoDataModal] = useState<boolean>(false);

    const realSeries = chartDataState.series.filter((serie): serie is BubbleSerie => serie.kind === "real");

    const hasNoPersonData =
        realSeries.length > 0 &&
        realSeries.every((serie) => {
            return serie.data?.every(
                (point) =>
                    point?.custom?.entity_type === "person" && point?.x == null && point?.y == null && point?.z == null
            );
        });

    useEffect(() => {
        if (hasNoPersonData) {
            setShowNoDataModal(true);
        }
    }, [hasNoPersonData]);

    const onCloseNoDataModal = () => {
        setShowNoDataModal(false);
    };

    const onClickChart = useCallback(
        (event: Highcharts.PointerEventObject) => {
            // Closes person work history when clicking on the chart
            onClose();

            // Draws a circle
            if (chartRef.current && getbullEyeCoors) {
                const circleConfig = {
                    x: chartRef.current.chart.xAxis[0].toPixels(event.xAxis[0].value, false),
                    y: chartRef.current.chart.yAxis[0].toPixels(event.yAxis[0].value, false),
                    radius: getProportionalRadius(
                        chartRef.current.chart.chartWidth,
                        moreOptions?.bullEyeCoors?.radius || 50
                    ),
                };

                if (!isEmpty(circleRef.current) && !isEmpty(crossHairRef.current)) {
                    circleRef.current?.destroy();
                    crossHairRef.current?.destroy();
                }

                circleRef.current = chartRef.current.chart.renderer
                    .circle(circleConfig.x, circleConfig.y, circleConfig.radius)
                    .attr(BRIEF_REFERENCE_CIRCLE_STYLES)
                    .add();

                crossHairRef.current = chartRef.current.chart.renderer
                    .image(crossHairImg, circleConfig.x - 11, circleConfig.y - 11, 22, 22)
                    .attr({ zIndex: BRIEF_REFERENCE_CIRCLE_STYLES.zIndex })
                    .add();

                setCircle({
                    x: event.xAxis[0].value,
                    y: event.yAxis[0].value,
                    radius: circleConfig.radius,
                });
            }
        },
        [getbullEyeCoors, moreOptions?.bullEyeCoors?.radius, onClose]
    );

    const [chartOptions, setChartOptions] = useState(
        getOption(
            {
                onClickChart,
                renderArchetypes,
                renderAxesTitles,
                renderDotCircles: renderDots,
                clickPoint,
                legendItemClick,
            },
            chartDataState,
            moreOptions
        )
    );

    const updateSeries = updateSeriesWrapper(isLegendClicked, setChartOptions, visibleSeries);
    const { chartExtraOptions, resizeReRender, onChartResize } = useChartResize(width, height, "balance");
    useCustomiseLabels(
        showRoles,
        showNames,
        setChartOptions,
        visibleSeries.current,
        showAllLabels,
        hideAllLabels,
        true
    );
    useChartRender(width, height, chartRef, [onChartResize, updateSeries]);

    const handleToggleArchetype = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setChartCircles(checked);
        toggleShowArchetypeVisibility(checked);
    };

    useEffect(() => {
        if (chartRef.current) {
            if (!isEmpty(circleRef.current) && !isEmpty(crossHairRef.current)) {
                circleRef.current?.destroy();
                crossHairRef.current?.destroy();
            }

            if (moreOptions?.bullEyeCoors?.x && moreOptions?.bullEyeCoors?.y) {
                const x = chartRef.current.chart.xAxis[0].toPixels(moreOptions?.bullEyeCoors?.x || 0, false);
                const y = chartRef.current.chart.yAxis[0].toPixels(moreOptions?.bullEyeCoors?.y || 0, false);
                const radius = getProportionalRadius(
                    chartRef.current.chart.chartWidth,
                    moreOptions?.bullEyeCoors?.radius || 50
                );

                circleRef.current = chartRef.current.chart.renderer
                    .circle(x, y, radius)
                    .attr({
                        ...BRIEF_REFERENCE_CIRCLE_STYLES,
                        stroke: "black",
                    })
                    .add();

                crossHairRef.current = chartRef.current.chart.renderer
                    .image(crossHairImg, x - 11, y - 11, 22, 22)
                    .attr({ zIndex: BRIEF_REFERENCE_CIRCLE_STYLES.zIndex })
                    .add();
            }

            if (chartRef.current && getbullEyeCoors && circle.radius && !isEqual(previousCircle, circle)) {
                getbullEyeCoors({ ...circle, radius: moreOptions?.bullEyeCoors?.radius || 50 });
            }
        }
    }, [moreOptions?.bullEyeCoors, circle, getbullEyeCoors, previousCircle, resizeReRender]);

    useEffect(() => {
        if (chartRef.current && moreOptions?.elementsToDraw?.length) {
            moreOptions?.elementsToDraw.forEach((element, i) => {
                if (elementsToDrawRef.current[i]) {
                    elementsToDrawRef.current[i]?.destroy();
                }
                elementsToDrawRef.current[i] = chartRef.current?.chart.renderer
                    .circle(
                        chartRef.current.chart.xAxis[0].toPixels(element?.x || 0, false),
                        chartRef.current.chart.yAxis[0].toPixels(element?.y || 0, false),
                        getProportionalRadius(chartRef.current.chart.chartWidth, element?.radius || 1)
                    )
                    .attr({
                        ...BRIEF_REFERENCE_CIRCLE_STYLES,
                        stroke: theme.palette.neutrals.main,
                    })
                    .add();
            });
        }
    }, [moreOptions?.bullEyeCoors, moreOptions?.elementsToDraw, resizeReRender]);

    useEffect(() => {
        if (fromRefinement) setShowRoles(false);
    }, [fromRefinement]);

    useEffect(() => {
        // useEffect to change the chart dimensions when the dimensions of its parent changes.
        // The resizeReRender dependency can change after highchart executes the callbacks for the render event
        if (!chartRef.current?.chart) return;

        setChartOptions((currentValue) => {
            const { chart, credits, legend, exporting, ...currentValueRest } = currentValue;

            return {
                ...currentValueRest,
                chart: {
                    ...chart,
                    ...chartExtraOptions.current?.chart,
                    events: {
                        ...chart?.events,
                        click: function (this, event) {
                            onClickChart(event);
                        },
                        redraw: function (this) {
                            renderDots(this as Highcharts.Chart);
                            renderArchetypes(this as Highcharts.Chart, chartDataState, moreOptions);
                            renderAxesTitles(this as Highcharts.Chart, chartDataState, moreOptions);
                            if (moreOptions?.bullEyeCoors) {
                                this.container.style.cursor = CROSS_HAIR_CURSOR;
                            }
                        },
                    },
                },
                credits: {
                    ...credits,
                    position: {
                        ...credits?.position,
                        ...chartExtraOptions.current?.credits?.position,
                    },
                },
                series: chartDataState.series.map((serie, index) => {
                    if (!isBubbleSerie(serie)) {
                        return serie;
                    }

                    return {
                        ...serie,
                        visible: visibleSeries.current.includes(index) ? true : false,
                    };
                }),
                exporting: {
                    ...exporting,
                    chartOptions: {
                        chart: {
                            events: {
                                load: function (this) {
                                    renderDots(this as Highcharts.Chart, true);
                                    renderArchetypes(this as Highcharts.Chart, chartDataState, moreOptions, true);
                                    renderAxesTitles(this as Highcharts.Chart, chartDataState, moreOptions, true);
                                },
                            },
                        },
                    },
                },
                legend: {
                    ...legend,
                    ...chartExtraOptions.current?.legend,
                },
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartExtraOptions, resizeReRender, width, height, chartCircles, onClickChart, renderDots]);

    return [
        {
            chartOptions,
            chartData: chartDataState,
            chartRef,
            showNoDataModal,
            chartCircles,
            personDataElement,
            peopleDataElement,
            containerRef,
            showRoles,
            showNames,
        },
        {
            onClose,
            setChartCircles,
            setShowRoles,
            setShowNames,
            showAllLabels,
            setShowAllLabels,
            hideAllLabels,
            setHideAllLabels,
            handleToggleArchetype,
            onCloseNoDataModal,
        },
    ];
};

export default useBalance;
